import { buildCdnImageUrlAdapterFor } from '@lmn/lmn-components';

import type { IconColor } from './Icon';
import type { StarfishCategory, StarfishIconsByCategory } from './starfishManifest';

/**
 * URL construction for the Starfish icon set on the ImageKit DAM.
 *
 * Two rules the delivery enforces silently, both answered here rather than at
 * call sites:
 *
 * - An icon is identified by its (category, name) pair. Folder names are
 *   case-sensitive, and three names exist in two categories each with different
 *   artwork behind them, so a name-only lookup would resolve the wrong glyph
 *   without any error. The generated manifest types the pair.
 * - Colour has to be applied to a rasterised copy. `e-colorize` on an SVG with
 *   no `f-png` returns the untouched artwork with HTTP 200, so the colour
 *   silently does not apply. The design system adapter injects `f-png` for us
 *   whenever a colour is set on an `.svg`, which is why this module goes
 *   through the adapter instead of assembling the query string by hand.
 */

const DAM_PATH = ['DAM', 'UX', "Flip Flop's Design System assets", 'Starfish icons'];

// encodeURIComponent leaves the apostrophe alone; the DAM and every URL Design
// shares use %27, so normalise to that.
const encodeSegment = (segment: string): string => encodeURIComponent(segment).replace(/'/g, '%27');

export const STARFISH_BASE = `https://media.staticroot.com/${DAM_PATH.map(encodeSegment).join('/')}`;

export const starfishUrl = <C extends StarfishCategory>(category: C, name: StarfishIconsByCategory[C]): string =>
  `${STARFISH_BASE}/${encodeSegment(category)}/${encodeSegment(name)}.svg`;

/**
 * Fully-built URL for CSS contexts, where `CdnImage` cannot be used.
 *
 * With no colour this is the untransformed `.svg`, which is what a
 * `mask-image` wants: the mask reads the alpha channel and the colour comes
 * from `background-color`. Pass a colour only for `background-image`, where
 * the artwork itself has to carry it — and the size it is drawn at, so the
 * rasterised copy is requested at twice that, as the Icon component does, instead of
 * at the 24px source, which is soft on a retina screen once drawn larger.
 */
export const starfishCssUrl = <C extends StarfishCategory>(
  category: C,
  name: StarfishIconsByCategory[C],
  hex?: string,
  drawnPx?: number
): string =>
  buildCdnImageUrlAdapterFor('imageKit').adaptUrl({
    url: starfishUrl(category, name),
    transformations: hex
      ? {
          color: hex,
          ...(drawnPx && { size: { type: 'fill', widthInPx: drawnPx * 2, heightInPx: drawnPx * 2 } }),
        }
      : {},
  }).url;

/**
 * An icon named rather than resolved, so the colour and the drawn size are decided by whoever
 * renders it, where the theme is in reach.
 */
export type StarfishIconRef<C extends StarfishCategory = StarfishCategory> = {
  [K in C]: {
    category: K;
    name: StarfishIconsByCategory[K];
    color?: IconColor;
    /**
     * How much bigger than its slot the glyph is drawn, so it carries the ink of the asset it
     * replaced there (that asset's ink fraction over the Starfish glyph's). The slot is kept.
     */
    inkScale?: number;
  };
}[C];
